import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, categories, comics, siteInfo } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (id) {
      const found = getComicById(id);
      setComic(found);
    }
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-24 min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-2 text-foreground-500">
            <i className="ri-loader-4-line animate-spin"></i>
            <span>加载中...</span>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩漫网" />
        <Navbar />
        <main className="pt-24 min-h-screen flex flex-col items-center justify-center">
          <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">您查找的漫画不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryObj = categories.find((c) => c.slug === comic.categorySlug);
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || '',
    dateModified: comic.updateDate,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${comic.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_韩漫网免费在线观看`}
        description={`${comic.title} - ${comic.description.substring(0, 120)}... 韩漫网免费韩国漫画下拉式在线观看`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-12 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/all" className="hover:text-primary-600 transition-colors whitespace-nowrap">全部漫画</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {categoryObj && (
              <>
                <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{categoryObj.name}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-800 font-medium truncate">{comic.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-full md:w-[280px] aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫网免费韩国漫画`}
                  title={`${comic.title} - 韩国漫画 韩漫网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <i className="ri-star-fill text-xs"></i>
                  {comic.rating} 分
                </span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600">
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                </span>
                <span className="text-foreground-300">|</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Link
                  to={`/article/${comic.id}/read`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 bg-background-200 hover:bg-background-300 text-foreground-700 px-5 py-3 rounded-lg font-medium text-sm transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
                <button className="inline-flex items-center gap-2 bg-background-200 hover:bg-background-300 text-foreground-700 px-5 py-3 rounded-lg font-medium text-sm transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/article/${comic.id}/read?chapter=${chapter.chapterNumber}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-700 font-medium truncate">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 ml-2 whitespace-nowrap">
                    {chapter.updateDate}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-16">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                推荐阅读
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedComics.map((rel) => (
                  <ComicCard key={rel.id} comic={rel} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}